/**
 * useControllableState
 * value && defaultValue 相关 - 自控和外部控制结合的封装
 *
 * Created by Samuel on 2022/6/2
 */
import * as _ from 'lodash-es';
import { useCallback, useState } from 'react';

import { useCallbackRef } from '@core/hooks/useCallbackRef';

const _DEFAULT_GET_VALUE = (a,b) => a;
// 默认情况下，返回的setValue的第一个值就是最终需要进行设置的值
const useUncontrolledState = ({ defaultProp, onChange, getValue = _DEFAULT_GET_VALUE }) => {
	const [value, setValue] = useState(defaultProp);
	const handleChange = useCallbackRef(onChange);

	const setUncontrolledValue = useCallback((...args) => {
		const value = getValue(...args);
		setValue((prevProps) => {
			// 使用之前的值和当前值进行对比，改变时触发onChange事件
			if (prevProps !== value) {
				const [e, ...extArgs] = args;
				// onChange 事件固定拆分传入参数为 [e, ...ext], 最终传递参数为 [e, value, ...ext]
				handleChange(e, value, ...extArgs);
			}
			return value;
		});
	}, []);

	return [value, setUncontrolledValue];
};

export const useControllableState = ({ prop, defaultProp, onChange = _.noop, getValue = _DEFAULT_GET_VALUE }) => {
	const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
		defaultProp,
		onChange,
		getValue,
	});
	const isControlled = prop !== undefined;
	const value = isControlled ? prop : uncontrolledProp;
	const handleChange = useCallbackRef(onChange);

	const setControllableValue = useCallback(
		(...args) => {
			if (isControlled) {
				const value = getValue(...args);
				if (value !== prop) {
					const [e, ...extArgs] = args;
					// onChange 事件固定拆分传入参数为 [e, ...ext], 最终传递参数为 [e, value, ...ext]
					handleChange(e, value, ...extArgs);
				}
			} else {
				setUncontrolledProp(...args);
			}
		},
		[isControlled, prop, setUncontrolledProp, handleChange],
	);

	return [value, setControllableValue];
};
